import { ScrollColumnView } from '@ipfabric/ui-components/ScrollView/ScrollColumnView';
import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type {
  DefinedIntentVerificationRuleSummary,
  IntentRuleId,
  IntentVerificationListGroup,
  ReportSeverityDescriptions,
  ReportSeverityResults,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { Accordion } from 'components/Accordion/Accordion';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { checkIntent } from 'components/Graph/commands/checkIntent';
import { SeverityCountsListItem } from 'components/SeverityCounts/SeverityCountsListItem';
import { useLocale } from 'containers/App/LocaleContext';
import { constVoid } from 'fp-ts/lib/function';
import { type FormEvent, useMemo, useState } from 'react';
import type { FC } from 'react';
import { compareCaseInsensitive } from 'utils/compareCaseInsensitive';
import { trimValueLeft } from 'utils/formValidation/trimValue';
import { SearchIcon } from '../icons/SearchIcon';
import { TimesIcon } from '../icons/TimesIcon';
import {
  type IntentVerificationListSearchResult,
  IntentVerificationListSearchResults,
} from './IntentVerificationListSearchResults';

const searchList = (
  list: IntentVerificationListGroup<
    ReportSeverityResults,
    ReportSeverityDescriptions
  >[],
  search: string,
): IntentVerificationListSearchResult[] => {
  const matchSearch = compareCaseInsensitive(search);
  return list.reduce(
    (
      acc: IntentVerificationListSearchResult[],
      item: IntentVerificationListGroup<
        ReportSeverityResults,
        ReportSeverityDescriptions
      >,
    ): IntentVerificationListSearchResult[] => {
      const foundChildren = item.children.filter((child) =>
        matchSearch(child.name),
      );
      if (foundChildren.length) {
        return acc.concat(
          foundChildren.map((child) => ({
            childId: child.id,
            descriptions: child.descriptions,
            childTitle: child.name,
            groupTitle: item.name,
            results: child.results,
          })),
        );
      }
      return acc;
    },
    [],
  );
};

interface Props {
  data: {
    loadingState: LoadingState;
    summary: DefinedIntentVerificationRuleSummary | null;
  };
  selectedIntentRuleId: IntentRuleId | null;
}

export const IntentVerificationList: FC<Props> = (props) => {
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
  });
  const [searchValue, setSearchValue] = useState<string>('');
  const msg = useLocale();

  const searchResults = searchValue
    ? searchList(props.data.summary?.list ?? [], searchValue)
    : [];

  const panels = useMemo(() => {
    const list = props.data.summary?.list ?? [];

    return list.map((item) => ({
      key: item.id,
      label: (
        <SeverityCountsListItem
          className={classNames(
            'severity-counts-list-item--intents',
            'severity-counts-list-item--collabsible-label',
            item.children.some(
              (child) => props.selectedIntentRuleId === child.id,
            ) && 'severity-counts-list-item--intents-selected-within',
          )}
          name={item.name}
          onClick={constVoid} // click is handled by accordion, this just adds clickable style
          counts={item.results}
        />
      ),
      content: (
        <>
          {item.children.map((child) => (
            <SeverityCountsListItem
              className={classNames(
                'severity-counts-list-item--intents',
                'severity-counts-list-item--collabsible-content',
                props.selectedIntentRuleId === child.id &&
                  'severity-counts-list-item--intents-selected',
              )}
              key={child.id}
              name={child.name}
              onClick={() => {
                ctxProps.dispatch(checkIntent, { intentRule: child.id });
              }}
              counts={child.results}
              descriptions={child.descriptions.checks}
              tooltip={child.descriptions.general}
            />
          ))}
        </>
      ),
    }));
  }, [props.data, props.selectedIntentRuleId, ctxProps.dispatch]);

  const handleSearchInputChange = (e: FormEvent<HTMLInputElement>) => {
    setSearchValue(e.currentTarget.value);
  };

  const handlClearSearchIconClick = () => {
    setSearchValue('');
  };

  return (
    <ScrollColumnView>
      <div className='mx-lgu-3 mt-lgu-3 relative flex flex-col'>
        <SearchIcon
          title={null}
          className={classNames(
            'ipf-input-search-icon',
            panels.length === 0 && 'ipf-input-search-icon--disabled',
          )}
          width={16}
          height={16}
        />
        <input
          className='ipf-input ipf-input--search'
          disabled={panels.length === 0}
          onChange={trimValueLeft(handleSearchInputChange)}
          placeholder={
            msg.canvasGraph.graphSidePanel.intentVerification
              .definedRulesSearchPlaceholder
          }
          value={searchValue}
        />
        {searchValue !== '' && (
          <div className='ipf-input-clear-icon'>
            <TimesIcon
              title='Clear search'
              height={10}
              onClick={handlClearSearchIconClick}
              width={9}
            />
          </div>
        )}
      </div>
      {props.data.summary?.list?.length === 0 && (
        <div className='pt-lgu-10 mx-5 flex flex-col items-center'>
          {
            msg.canvasGraph.graphSidePanel.intentVerification
              .noCustomChecksForDevices
          }
        </div>
      )}
      <div className='intent-verification-list pb-lgu-3'>
        {searchValue ? (
          <IntentVerificationListSearchResults searchResults={searchResults} />
        ) : (
          <Accordion
            panels={panels}
            backgroundHoverColorDark
            allowMultipleOpen
          />
        )}
      </div>
    </ScrollColumnView>
  );
};
